import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import Templates from '@/pages/Templates';
import TimeTracking from '@/pages/TimeTracking';
import Settings from '@/pages/Settings';
import Trash from '@/pages/Trash';
import Subscription from '@/pages/Subscription';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import AdminDashboard from '@/pages/AdminDashboard';
import AIResearch from '@/pages/AIResearch';
import AIBusinessCase from '@/pages/AIBusinessCase';
import ChangeManagement from '@/pages/ChangeManagement';
import PublicCRForm from '@/pages/PublicCRForm';
import ResourcePlanning from '@/pages/ResourcePlanning';
import RiskManagementPage from '@/pages/RiskManagementPage';
import ComingSoon from '@/pages/ComingSoon';
import { RequirementsCatalogListPage, RequirementsCatalogDetailPage } from '@/pages/RequirementsCatalogPage';
import VendorPortalPage from '@/pages/VendorPortalPage';
import { Toaster } from '@/components/ui/sonner';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { ModuleProvider } from '@/contexts/ModuleContext';
import { TimeTrackingProvider } from '@/contexts/TimeTrackingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user', error);
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    // Dispatch storage event to trigger module refresh
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'token',
      newValue: token
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    // Redirect to landing page
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl font-medium">Laden...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <SubscriptionProvider user={user}>
          <ModuleProvider>
            <TimeTrackingProvider>
            <Routes>
            {/* Public Routes - No Auth Required */}
            <Route
              path="/cr-form/:token"
              element={<PublicCRForm />}
            />
            <Route
              path="/login"
              element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/dashboard" />}
            />
            {/* Landing Page - Show to non-logged in users */}
            <Route
              path="/"
              element={!user ? <LandingPage /> : <Navigate to="/dashboard" />}
            />
            {/* Dashboard - Main app entry for logged in users */}
            <Route
              path="/dashboard"
              element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/projects"
              element={user ? <Projects user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/projects/:id"
              element={user ? <ProjectDetail user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/templates"
              element={user ? <Templates user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/time-tracking"
              element={user ? <TimeTracking user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/settings"
              element={user ? <Settings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/trash"
              element={user ? <Trash user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/subscription"
              element={user ? <Subscription user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/subscription/success"
              element={user ? <SubscriptionSuccess user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/admin"
              element={user ? <AdminDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/research"
              element={user ? <AIResearch user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/business-case"
              element={user ? <AIBusinessCase user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/change-management"
              element={user ? <ChangeManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/resource-planning"
              element={user ? <ResourcePlanning user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            {/* Coming Soon Routes for teased modules */}
            <Route
              path="/risk-management"
              element={user ? <RiskManagementPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/ai-assistant"
              element={user ? <ComingSoon user={user} onLogout={handleLogout} moduleName="KI-Projektassistent" /> : <Navigate to="/login" />}
            />
            <Route
              path="/advanced-pdf-export"
              element={user ? <ComingSoon user={user} onLogout={handleLogout} moduleName="Erweiterter PDF-Export" /> : <Navigate to="/login" />}
            />
            <Route
              path="/project-phases"
              element={user ? <ComingSoon user={user} onLogout={handleLogout} moduleName="Projektphasen-Management" /> : <Navigate to="/login" />}
            />
            <Route
              path="/requirements-catalog"
              element={user ? <RequirementsCatalogListPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            <Route
              path="/requirements-catalog/:id"
              element={user ? <RequirementsCatalogDetailPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
            />
            {/* Public Vendor Portal - no login required */}
            <Route
              path="/vendor-portal/:token"
              element={<VendorPortalPage />}
            />
          </Routes>
            </TimeTrackingProvider>
          </ModuleProvider>
        </SubscriptionProvider>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}

export default App;
